import React from "react";
import { Helmet } from "react-helmet-async";
import { Phone, Mail, Clock, MapPin, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { hreflangLinks, isFrPath, enTwinFor, SITE_BASE_URL } from "@/i18n/paths";
import { useLocation } from "react-router-dom";
import { FadeIn, MaskedReveal } from "@/components/Reveal";
import { CONTACT_PHONE, CONTACT_PHONE_TEL, CONTACT_EMAIL } from "@/components/Nav";
import { TESTIDS } from "@/constants/testIds";

export default function Contact() {
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  const enPath = enTwinFor(pathname);
  const fr = lang === "fr";

  return (
    <>
      <Helmet>
        <title>{t("meta.contact_title")}</title>
        <meta name="description" content={t("meta.contact_desc")} />
        <link rel="canonical" href={`${SITE_BASE_URL}${isFrPath(pathname) ? pathname : enPath}`} />
        {(hreflangLinks(enPath) || []).map((l) => (
          <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
        ))}
      </Helmet>

      <section className="px-6 md:px-10 pt-24 md:pt-32 pb-16 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-amber-500" />
          {t("nav.contact")}
        </div>
        <h1 className="font-display uppercase font-black text-5xl md:text-7xl lg:text-[9vw] tracking-tight leading-[0.88] text-white">
          <MaskedReveal>
            <span>{fr ? "Parlez-nous." : "Talk to us."}</span>
          </MaskedReveal>
        </h1>
        <FadeIn delay={0.2}>
          <p className="mt-8 text-zinc-400 text-lg max-w-2xl">
            {lang === "fr"
              ? "Appelez-nous ou envoyez-nous un courriel. Nos consultants sécurité bilingues répondent en un jour ouvrable."
              : "Call us or send us an email. Our bilingual security consultants reply within one business day."}
          </p>
        </FadeIn>
      </section>

      <section className="px-6 md:px-10 pb-24 max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <FadeIn>
          <a
            href={`tel:${CONTACT_PHONE_TEL}`}
            data-testid="contact-phone-card"
            className="group block border border-white/10 hover:border-amber-500 bg-zinc-950 hover:bg-black transition-colors duration-500 p-8 md:p-12 h-full relative overflow-hidden"
          >
            <div className="flex items-start justify-between mb-8">
              <div className="w-14 h-14 bg-amber-500 text-black flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                <Phone className="w-6 h-6" strokeWidth={2.2} />
              </div>
              <ArrowUpRight className="w-6 h-6 text-zinc-600 group-hover:text-amber-500 transition-all group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-500 mb-4">◉ {fr ? "Appelez-nous" : "Call us"}</div>
            <div className="font-display uppercase font-black text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none text-white group-hover:text-amber-500 transition-colors">
              {CONTACT_PHONE}
            </div>
            <p className="mt-6 text-zinc-400 text-sm md:text-base max-w-md">
              {lang === "fr"
                ? "Ligne directe pour les demandes de sécurité, les urgences et les soumissions. Bilingue FR/EN."
                : "Direct line for security enquiries, emergencies, and quote requests. Bilingual FR/EN."}
            </p>
          </a>
        </FadeIn>

        <FadeIn delay={0.05}>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="contact-email-card"
            className="group block border border-white/10 hover:border-amber-500 bg-zinc-950 hover:bg-black transition-colors duration-500 p-8 md:p-12 h-full relative overflow-hidden"
          >
            <div className="flex items-start justify-between mb-8">
              <div className="w-14 h-14 bg-amber-500 text-black flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                <Mail className="w-6 h-6" strokeWidth={2.2} />
              </div>
              <ArrowUpRight className="w-6 h-6 text-zinc-600 group-hover:text-amber-500 transition-all group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-500 mb-4">◉ {fr ? "Écrivez-nous" : "Email us"}</div>
            <div className="font-display uppercase font-black text-3xl md:text-4xl lg:text-5xl tracking-tight leading-none text-white group-hover:text-amber-500 transition-colors break-all">
              {CONTACT_EMAIL}
            </div>
            <p className="mt-6 text-zinc-400 text-sm md:text-base max-w-md">
              {lang === "fr"
                ? "Envoyez-nous les détails de votre site ou de votre événement. Réponse d'un consultant en un jour ouvrable."
                : "Send us the details of your site or event. A consultant replies within one business day."}
            </p>
          </a>
        </FadeIn>
      </section>

      <section className="border-t border-white/10 px-6 md:px-10 py-16 md:py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-8">→ {fr ? "Centre de commandement" : "Command centre"}</div>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <FadeIn>
            <div className="border border-white/10 p-8 h-full">
              <Clock className="w-6 h-6 text-amber-500 mb-6" strokeWidth={1.5} />
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{fr ? "Délai de réponse" : "Reply window"}</div>
              <div className="font-display uppercase text-2xl tracking-tight text-white">{fr ? "En un jour ouvrable" : "Within one business day"}</div>
              <p className="mt-4 text-sm text-zinc-400">{fr ? "Répartition d'urgence 24/7. Intervention sur alarme vérifiée en moins de 12 minutes." : "24/7 emergency dispatch. Sub-12-minute verified alarm response."}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.05}>
            <div className="border border-white/10 p-8 h-full">
              <MapPin className="w-6 h-6 text-amber-500 mb-6" strokeWidth={1.5} />
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{fr ? "Couverture" : "Coverage"}</div>
              <div className="font-display uppercase text-2xl tracking-tight text-white">{fr ? "Chaque municipalité du Québec" : "Every Quebec municipality"}</div>
              <p className="mt-4 text-sm text-zinc-400">{fr ? "17 régions, déploiement en moins de 45 minutes partout en province." : "17 regions, sub-45-minute deployment reach across the province."}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="border border-white/10 p-8 h-full">
              <div className="w-6 h-6 flex items-center justify-center text-amber-500 mb-6 font-mono font-bold text-sm">FR/EN</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">{fr ? "Langue" : "Language"}</div>
              <div className="font-display uppercase text-2xl tracking-tight text-white">{fr ? "Bilingue FR / EN" : "Bilingual FR / EN"}</div>
              <p className="mt-4 text-sm text-zinc-400">{fr ? "Niveau CECR B2 minimum dans tout l'effectif. C1 sur les postes en contact avec les locataires." : "CEFR B2 minimum roster-wide. C1 on tenant-facing posts."}</p>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
